import { useEffect, useState } from "react";
import { Modal } from "react-bootstrap";
import { toast } from "react-toastify";
import { getRequest, postRequest } from "../../../utils/api";
import { logout } from "../../../utils/auth";
import ActionModalHeader from "../../Users/modals/ActionModalHeader";
import Pill from "../../../Component/Pill";
import { affiliateName, tierRates } from "../components/AffiliateLayout";

interface AffiliateCommissionModalProps {
    show: boolean;
    onHide: () => void;
    affiliateData: any;
    onSuccess?: () => void;
}

// Change the commission tier of one affiliate (/affiliate/change-affiliate-commission-tier)
const AffiliateCommissionModal = ({ show, onHide, affiliateData, onSuccess }: AffiliateCommissionModalProps) => {
    const [isLoader, setIsLoader] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [commissionList, setCommissionList] = useState<any[]>([]);
    const [commissionId, setCommissionId] = useState("");

    // Fetch Commission Tiers
    const getCommissionTierList = async () => {
        try {
            setIsLoader(true);
            const response = await getRequest("/affiliate/affiliate-commission-tier-list");

            if (response.status === "success") {
                setCommissionList(response.data || []);
            } else if (response.status === "session_expired") {
                logout();
            } else {
                toast.error(response.message);
            }
        } catch (error) {
            toast.error("Something went wrong loading commission tiers!");
        } finally {
            setIsLoader(false);
        }
    };

    useEffect(() => {
        if (show && affiliateData) {
            // affiliate_referral_commission_id is an id in the list and a populated tier in the details drawer
            const current = affiliateData.affiliate_referral_commission_id;
            setCommissionId(current?._id || current || "");
            getCommissionTierList();
        }
    }, [show, affiliateData]);

    const handleSubmit = async () => {
        if (!commissionId) return toast.warning("Please select a commission tier");

        try {
            setIsSaving(true);
            const response = await postRequest("/affiliate/change-affiliate-commission-tier", {
                affiliateId: affiliateData._id,
                commissionId: commissionId,
            });

            if (response.status === "success") {
                toast.success(response.message);
                onSuccess?.();
                onHide();
            } else if (response.status === "session_expired") {
                logout();
            } else {
                toast.error(response.message);
            }
        } catch (error: any) {
            toast.error(typeof error === "string" ? error : "Something went wrong");
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <Modal show={show} onHide={onHide} centered contentClassName="cx">
            <ActionModalHeader
                icon="ri-percent-line"
                title="Change commission tier"
                subtitle={<>{affiliateName(affiliateData)} · applies to commissions earned from now on</>}
                onClose={onHide}
            />
            <div style={{ padding: "14px 20px", maxHeight: 360, overflowY: "auto" }}>
                {isLoader ? (
                    <div className="cx-empty" style={{ padding: 24 }}>Loading tiers…</div>
                ) : commissionList.length ? commissionList.map(item => (
                    <label key={item._id} className="cx-orow click" style={{ alignItems: "center", marginBottom: 0 }}>
                        <input type="radio" name="commission-tier" checked={commissionId === item._id} onChange={() => setCommissionId(item._id)} />
                        <div>
                            <div className="t">{item.commission_title}</div>
                            <div className="d">
                                {item.commission_amount}% of plan invoices (before tax) · {Number(item.commission_messaging_amount || 0)}% of messaging profit
                                {Number(item.commission_min_users) > 0 && <> · min. {item.commission_min_users} users</>}
                            </div>
                        </div>
                        <div className="right">
                            {Number(item.commission_publish_type) === 1 && <Pill tone="blue">Default</Pill>}
                            <b className="mono">{tierRates(item)}</b>
                        </div>
                    </label>
                )) : (
                    <div className="cx-empty" style={{ padding: 24 }}>No commission tiers yet — add one from the Commission tiers tab.</div>
                )}
            </div>
            <div style={{ padding: "0 20px 18px", display: "flex", justifyContent: "flex-end", gap: 8 }}>
                <button type="button" className="cx-btn cx-btn-light" onClick={onHide}>Cancel</button>
                <button type="button" className="cx-btn cx-btn-primary" onClick={handleSubmit} disabled={isSaving || !commissionId}>
                    {isSaving ? "Saving…" : "Save tier"}
                </button>
            </div>
        </Modal>
    );
};

export default AffiliateCommissionModal;
